import { EmailInput, Modal } from "components";
import CulturesTemplateDropdown from "components/dropdowns/reactselect/CulturesTemplateDropdown";
import { LocalFormSectionTemplate } from "components/MainSectionTemplate/LocalFormSectionTemplate";
import CompanyModalPicker from "components/Picker/CompanyPicker/CompanyModalPicker";
import ContactParticipantModalPicker, { ContactParticipant } from "components/Picker/ContactParticipantPicker/ContactParticipantModalPicker";
import { RemovableLabel } from "components/Removables/Removable";
import React from "react";
import { isNullOrEmptyString } from "shared/utils";
import { messageTemplateForESignSigner } from "utilities/documentUtils";
import { getCultureFromLcid } from "utilities/enumutils";

interface Props {
  documentType: server.dto.DocumentTypes;
  documentId: number;
  onChange: (participant: ContactParticipant) => void;
  onClose: () => void;
  hasError: boolean;
  senderOrSigner: "sender" | "signer";
  startOpen: boolean;
}

export const SelectSenderSignerModal = (props: Props) => {
  const [selectedParticipant, setSelectedParticipant] = React.useState<ContactParticipant>(undefined);

  const onContactParticipantSelected = (participant: ContactParticipant) => {
    if (isNullOrEmptyString(participant.email) || participant.company == null) {
      setSelectedParticipant(participant);
    } else {
      props.onChange(participant);
    }
  }

  if (!selectedParticipant) {
    return <ContactParticipantModalPicker
      canCreateNew={false}
      documentId={props.documentId}
      mainArea={props.documentType}
      hasCompany
      messageTemplateType={messageTemplateForESignSigner(props.documentType)}
      nonInPageEditing={{
        onChange: onContactParticipantSelected,
        startOpen: props.startOpen,
        value: null,
        validatorKey: "participantContact",
        required: false,
        legacyHasError: props.hasError,
      }}
    />
  }

  const label = props.senderOrSigner === "sender" ? RESX.ESign.Sender : RESX.ESign.Signer;

  return <Modal
    title={label}
    isOpen
    handleClose={props.onClose}
    width="small"
  >
    <LocalFormSectionTemplate
      onSuccess={() => props.onChange(selectedParticipant)}
      sections={[{
        key: "participantDetails",
        type: "regular",
        title: label,
        fields: [{
          key: "name",
          label: RESX.Contact.resxContact,
          renderEditNew: () => <RemovableLabel
            onChange={() => setSelectedParticipant(undefined)}
            value={selectedParticipant.contact}
            readonly={false}
            error={false}
          >
            {selectedParticipant.contact.name.fullName}
          </RemovableLabel>,
        }, {
          key: "representing",
          label: RESX.ESign.Representing,
          renderEditNew: () => <CompanyModalPicker
            modalTitle={RESX.Company.resxCompany}
            addressType="administrative"
            canCreateNew={false}
            hideEditButton
            nonInPageEditing={{
              onChange: (company) => setSelectedParticipant({ ...selectedParticipant, company }),
              value: selectedParticipant.company,
              required: true,
              validatorKey: "participantCompany",
            }}
          />
        }, {
          key: "email",
          label: RESX.GeneralLabels.Email,
          renderEditNew: () => <EmailInput
            value={selectedParticipant.email}
            onChange={(email) => setSelectedParticipant({ ...selectedParticipant, email })}
            emailCanBeBlank={false}
            validatorKey="participantEmail"
          />
        }, {
          key: "culture",
          label: RESX.Language.resxLanguage,
          renderEditNew: () => <CulturesTemplateDropdown
            value={selectedParticipant.culture.value}
            onChange={(lcid) => setSelectedParticipant({ ...selectedParticipant, culture: getCultureFromLcid(lcid) })}
          />
        }]
      }]}
    />
  </Modal>

}
